import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Movie } from '../../types/movie';
import DescriptionModal from '../common/DescriptionModal';
import styles from './MovieCard.module.css';

interface MovieCardProps {
  movie: Movie;
  index?: number;
}

const MovieCard: React.FC<MovieCardProps> = ({ movie, index = 0 }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [maxDescriptionLength, setMaxDescriptionLength] = useState(90);
  
  // 监听窗口大小变化，动态调整描述文本长度
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 375) {
        setMaxDescriptionLength(60);
      } else if (window.innerWidth <= 576) {
        setMaxDescriptionLength(75);
      } else {
        setMaxDescriptionLength(90);
      }
    };
    
    handleResize(); // 初始化
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // 将 freeEpisodes 转换为数字，如果是字符串或 undefined，则处理它
  const freeEpisodesCount = typeof movie.freeEpisodes === 'string' 
    ? parseInt(movie.freeEpisodes, 10) 
    : (movie.freeEpisodes || 0);
    
  // 判断是否为短剧（episodes > 1为短剧，≤1为短视频）
  const isDrama = movie.episodes > 1;
    
  const openModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  };
  
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // 截断描述，确保显示合理长度
  const truncateDescription = (text: string) => {
    if (!text) {return "暂无描述";}
    if (text.length <= maxDescriptionLength) {return text;}
    return text.substring(0, maxDescriptionLength).trim() + "...";
  };
  
  // 导航到搜索页面
  const navigateToGenre = (e: React.MouseEvent, genre: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = `/search?genre=${encodeURIComponent(genre)}`;
  };

  return (
    <>
      <motion.div 
        className={styles.card}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ y: -10, transition: { duration: 0.2 } }}
      >
        <div className={styles.cardContent}>
          <a href={`details?id=${movie._id}&episode=1`} className={styles.cardLink}>
            <div className={styles.imageContainer}>
              <img 
                src={movie.coverImageH} 
                alt={movie.title} 
                className={styles.image}
                loading="lazy"
              />
              <div className={styles.overlay}>
                <div className={styles.playButton}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 3L19 12L5 21V3Z" fill="white"/>
                  </svg>
                </div>
              </div>
              
              {/* 只为短剧显示genres，放置在图片区域左上角 */}
              {isDrama && movie.genres.length > 0 && (
                <div className={styles.imageGenres}>
                  {movie.genres.map((genre, i) => (
                    <span 
                      key={i} 
                      onClick={(e) => navigateToGenre(e, genre)}
                      className={styles.genre}
                    >
                      {genre}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </a>
          
          <div className={styles.cardInfo}>
            <a href={`details?id=${movie._id}&episode=1`} className={styles.titleLink}>
              <h3 className={styles.title}>{movie.title}</h3>
            </a>
            
            {/* 为短剧（episodes > 1）添加描述，并使描述可点击跳转 */}
            {isDrama && movie.description && (
              <div className={styles.descriptionContainer}>
                <a href={`details?id=${movie._id}&episode=1`} className={styles.descriptionLink}>
                  <p className={styles.description}>
                    {truncateDescription(movie.description)}
                  </p>
                </a>
              </div>
            )}
            
            <div className={styles.meta}>
              <span className={styles.year}>{movie.year}</span>
              <span className={styles.separator}>•</span>
              <span className={styles.duration}>{movie.duration}</span>
              {movie.episodes > 1 && (
                <>
                  <span className={styles.separator}>•</span>
                  <span className={styles.episodes}>{movie.episodes} EP</span>
                </>
              )}
            </div>
            
            {/* 免费集数和分数与More details按钮并排展示 */}
            <div className={styles.infoFooter}>
              <div className={styles.badgesContainer}>
                {freeEpisodesCount > 0 && (
                  <span className={styles.freeBadgeText}>
                    {freeEpisodesCount} Free
                  </span>
                )}
                <span className={styles.scoreBadgeText}>
                  {movie.score}
                </span>
              </div>
              
              {isDrama && movie.description && (
                <button 
                  className={styles.moreDetailsButton} 
                  onClick={openModal}
                >
                  More details
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      
      <DescriptionModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        movie={movie}
      />
    </>
  );
};

export default MovieCard; 